import React, { useState, useEffect } from "react";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import DownloadVideo from "./DownloadVideo";
import "../Download/DownloadSidebar.css";

function DownloadButton({
  wo,
  ImgUri,
  setDownloadProgress,
  downloadProgress,
  setArchivedImages,
  downloadImages,
  setIsCheckAll,
  isCheckAll,
  setIsCheck,
  isCheck,
  setIsDownloadSidebarVisible,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentDownloadList, setCurrentDownloadList] = useState([]);
  const [filesDownloaded, setFilesDownloaded] = useState(0);
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]); // Add this

   useEffect(() => {
    if (Object.keys(downloadProgress).length === 0) {
      setFailedFiles([]);
    }
  }, [downloadProgress]);

  useEffect(() => {
    setCurrentDownloadList(downloadImages || []);
  }, [isCheckAll, ImgUri, downloadImages]);

  const setProgress = (key, updates) =>
    setDownloadProgress((prev) => ({
      ...prev,
      [key]: {
        ...(typeof prev[key] === "object" ? prev[key] : {}),
        ...updates,
      },
    }));

  const markFailed = (key, error) => {
  setProgress(key, {
    status: "failed",
    error: (error && error.message) ? error.message : String(error || "Failed"),
    percent: null,
  });
  // Add failed file to list
  setFailedFiles((prev) => {
    if (!prev.includes(key)) {
      return [...prev, key];
    }
    return prev;
  });
};

  const markStarted = (key) =>
    setProgress(key, { status: "in-progress", percent: 0 });

  const markCompleted = (key) => {
  setProgress(key, { status: "completed", percent: 100 });
  // Remove from failedFiles if it was retried successfully
  setFailedFiles((prev) => prev.filter((fileurl) => fileurl !== key));
};

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const parseImage = (item) => {
    const s = typeof item?.image === "string" ? item.image : (typeof item === "string" ? item : "");
    const [fileurl = "", fileName = "download", ext = "", fileSize = ""] = s.split("$");
    const fileSizeNum = (() => {
      if (fileSize === undefined || fileSize === null || fileSize === "") return null;
      const n = Number(fileSize);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();
    return { fileurl, fileName, ext, fileSize, fileSizeNum };
  };

  const VIDEO_EXTENSIONS = ["mp4", "avi", "mov", "mkv", "flv", "wmv", "webm", "m4v", "hevc"];
  const isVideoFile = (ext) => !!(ext && VIDEO_EXTENSIONS.includes(ext.toLowerCase()));

  const downloadFile = async (fileurl, downloadFileName, downloadFileNameExtension) => {
    const res = await fetch(fileurl, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = downloadFileName.endsWith(`.${downloadFileNameExtension}`)
      ? downloadFileName
      : `${downloadFileName}.${downloadFileNameExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const processIndividualDownload = async (item) => {
    const { fileurl, fileName, ext } = parseImage(item);
    if (!fileurl) {
      markFailed(fileurl || item, new Error("Missing file URL"));
      return;
    }
    try {
      if (isVideoFile(ext)) {
        // use original DownloadVideo signature for videos when downloading individually
        await DownloadVideo(fileName, ext, fileurl, (progress) => {
          setProgress(fileurl, { percent: progress, status: "in-progress" });
        });
      } else {
        await downloadFile(fileurl, fileName, ext);
      }
      markCompleted(fileurl);
      setFilesDownloaded((n) => n + 1);
    } catch (err) {
      markFailed(fileurl, err);
      setFilesDownloaded((n) => n + 1);
    }
  };

  const startParallelDownloads = (filesToDownload) => {
    const CONCURRENCY = 3;
    let idx = 0;
    const workers = new Array(Math.min(CONCURRENCY, filesToDownload.length))
      .fill(null)
      .map(async () => {
        while (idx < filesToDownload.length) {
          const i = idx++;
          await processIndividualDownload(filesToDownload[i]);
          await sleep(50);
        }
      });
    return Promise.all(workers);
  };

    const handleRetryFailed = async () => {
    if (failedFiles.length === 0) return;

    const failedFilesBeforeRetry = [...failedFiles];
    setIsDownloading(true);
    setFilesDownloaded(0);
    setIsDownloadSidebarVisible(true);

    try {
      // Find the original items that failed
      const failedItems = currentDownloadList.filter((item) => {
        const { fileurl } = parseImage(item);
        return failedFilesBeforeRetry.includes(fileurl);
      });

      if (failedItems.length === 0) return;

      // Clear previous failed status
      failedItems.forEach((item) => {
        const { fileurl } = parseImage(item);
        markStarted(fileurl);
      });

      // Retry downloads in parallel
      await startParallelDownloads(failedItems);
      setFailedFiles((prev) => {      
      return prev.filter((fileurl) => failedFilesBeforeRetry.includes(fileurl));
    });
    } catch (err) {
      console.error("Error during retry:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentDownloadList || currentDownloadList.length === 0) return;

    setIsDownloading(true);
    setFilesDownloaded(0);
    setIsDownloadSidebarVisible(true);

    const filenames = new Set();
    const LARGE_THRESHOLD = 100 * 1024 * 1024; // 100 MB
    const ZIP_CHUNK_THRESHOLD = 1024 * 1024 * 1024; // ~1 GB
    const SELECTION_SIZE_THRESHOLD = 10; // if <=10, download all individually

    try {
      currentDownloadList.forEach((item) => {
        const { fileurl } = parseImage(item);
        if (fileurl) markStarted(fileurl);
      });

      // Classification rule: size-only (>100MB => individual). Missing size => ZIP-candidate.
      const largeFiles = [];
      const zipCandidates = [];
      currentDownloadList.forEach((item) => {
        const { fileSizeNum } = parseImage(item);
        if (fileSizeNum !== null && fileSizeNum > LARGE_THRESHOLD) {
          largeFiles.push(item);
        } else {
          zipCandidates.push(item);
        }
      });

      const downloadAllIndividually = currentDownloadList.length <= SELECTION_SIZE_THRESHOLD;

      if (downloadAllIndividually) {
        // <=10 items: download each individually. Videos use DownloadVideo.
        await startParallelDownloads(currentDownloadList);
      } else {
        // >10 items:
        // - files >100MB download individually in parallel (videos use DownloadVideo)
        // - remaining files (including videos <=100MB) are zipped sequentially
        const largeDownloadsPromise = startParallelDownloads(largeFiles);
        
        //sync zip slow due to compression
        // if (zipCandidates.length > 0) {
        //   setIsCreatingZip(true);
        //   let zip = new JSZip();
        //   let currentChunkSize = 0;
        //   let chunkIndex = 1;

        //   const addFileToZip = async (item) => {
        //     const { fileurl, fileName, ext, fileSizeNum } = parseImage(item);
        //     try {

        //       let blob;
        //       if(isVideoFile(ext)) {
        //         // for videos in zip, use DownloadVideo to get progress and retry logic, but capture the blob instead of triggering download
        //         const apiurl = process.env.REACT_APP_VIDEO_GETBLOB_API_URL;
        //         const blobURLSplit = fileurl.split("/");
        //         let blobName = blobURLSplit[blobURLSplit.length - 1];
        //         let fileExtension = ext;
        //         const response = await axios.get(apiurl, {
        //           params: {
        //             blobName,
        //             fileExtension
        //           },
        //           responseType: 'blob',
        //           onDownloadProgress: (progressEvent) => {
        //             const total = progressEvent.total;
        //             const current = progressEvent.loaded;
        //             const percentage = Math.round((current / total) * 100);
        //             setProgress(fileurl, { percent: percentage, status: "in-progress" });
        //           }
        //         });
        //         blob = new Blob([response.data]);
        //         setProgress(fileurl, { percent: 100, status: "in-progress" });                
        //       } else {
        //         // non-video files: fetch directly
        //       const res = await fetch(fileurl, { mode: "cors" });
        //       if (!res.ok) throw new Error(`HTTP ${res.status}`);
        //       blob = await res.blob();
        //       }
        //       const extension = ext || "";
        //       let baseName = fileName || "download";
        //       let uniqueName = baseName;
        //       let counter = 1;
        //       while (filenames.has(uniqueName + (extension ? "." + extension : ""))) {
        //         uniqueName = `${baseName}(${counter})`;
        //         counter++;
        //       }
        //       const fullName = extension ? `${uniqueName}.${extension}` : uniqueName;
        //       filenames.add(fullName);

        //       zip.file(fullName, blob);
        //       currentChunkSize += blob.size || (fileSizeNum || 0);
        //       setProgress(fileurl, { status: "added-to-zip", percent: null });
        //       // mark as completed for UI because it will be delivered inside a zip
        //       markCompleted(fileurl);
        //       setFilesDownloaded((n) => n + 1);
        //     } catch (err) {
        //       const { fileurl } = parseImage(item);
        //       markFailed(fileurl, err);
        //       setFilesDownloaded((n) => n + 1);
        //     }
        //   };

        //   for (let i = 0; i < zipCandidates.length; i++) {
        //     const item = zipCandidates[i];
        //     const { fileSizeNum } = parseImage(item);
        //     const sizeToAdd = fileSizeNum || 0;
        //     if (currentChunkSize + sizeToAdd > ZIP_CHUNK_THRESHOLD && currentChunkSize > 0) {
        //       try {
        //         const content = await zip.generateAsync({
        //           type: "blob",
        //           compression: "DEFLATE",
        //           compressionOptions: { level: 1 },
        //           streamFiles: true,
        //         });
        //         saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
        //       } catch (err) {
        //         // zip generation error: continue with new zip
        //         console.error("Error finalizing zip part:", err);
        //       }
        //       zip = new JSZip();
        //       currentChunkSize = 0;
        //       chunkIndex++;
        //     }
        //     await addFileToZip(item);
        //   }

        //   if (currentChunkSize > 0) {
        //     try {
        //       const content = await zip.generateAsync({
        //         type: "blob",
        //         compression: "DEFLATE",
        //         compressionOptions: { level: 1 },
        //         streamFiles: true,
        //       });
        //       saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
        //     } catch (err) {
        //       console.error("Error finalizing last zip part:", err);
        //     }
        //   }
        //   setIsCreatingZip(false);
        // }

        // Parallel file fetching without compression
        if (zipCandidates.length > 0) {
          setIsCreatingZip(true);
          let zip = new JSZip();
          let currentChunkSize = 0;
          let chunkIndex = 1;

          const addFileToZip = async (item) => {
            const { fileurl, fileName, ext, fileSizeNum } = parseImage(item);
            try {
              let blob;
              if(isVideoFile(ext)) {
                const apiurl = process.env.REACT_APP_VIDEO_GETBLOB_API_URL;
                const blobURLSplit = fileurl.split("/");
                let blobName = blobURLSplit[blobURLSplit.length - 1];
                let fileExtension = ext;
                const response = await axios.get(apiurl, {
                  params: { blobName, fileExtension },
                  responseType: 'blob',
                  onDownloadProgress: (progressEvent) => {
                    const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    setProgress(fileurl, { percent: percentage, status: "in-progress" });
                  }
                });
                blob = response.data;
              } else {
                const res = await fetch(fileurl, { mode: "cors" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                blob = await res.blob();
              }
              
              const extension = ext || "";
              let baseName = fileName || "download";
              let uniqueName = baseName;
              let counter = 1;
              while (filenames.has(uniqueName + (extension ? "." + extension : ""))) {
                uniqueName = `${baseName}(${counter})`;
                counter++;
              }
              const fullName = extension ? `${uniqueName}.${extension}` : uniqueName;
              filenames.add(fullName);

              zip.file(fullName, blob);
              currentChunkSize += blob.size || (fileSizeNum || 0);
              setProgress(fileurl, { status: "added-to-zip", percent: null });
              markCompleted(fileurl);
              setFilesDownloaded((n) => n + 1);
            } catch (err) {
              const { fileurl } = parseImage(item);
              markFailed(fileurl, err);
              setFilesDownloaded((n) => n + 1);
            }
          };

          // Process files in smaller batches
          const BATCH_SIZE = 10;
          for (let i = 0; i < zipCandidates.length; i += BATCH_SIZE) {
            const batch = zipCandidates.slice(i, i + BATCH_SIZE);
            
            await Promise.all(batch.map(item => addFileToZip(item)));
            
            const nextItem = zipCandidates[i + BATCH_SIZE];
            const nextSize = nextItem ? parseImage(nextItem).fileSizeNum || 0 : 0;
            
            if (currentChunkSize + nextSize > ZIP_CHUNK_THRESHOLD && currentChunkSize > 0) {
              try {
                const content = await zip.generateAsync({
                  type: "blob",
                  compression: "STORE",
                  // compressionOptions: { level: 1 },
                  streamFiles: true,
                });
                saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
              } catch (err) {
                console.error("Error finalizing zip part:", err);
              }
              zip = new JSZip();
              currentChunkSize = 0;
              chunkIndex++;
            }
          }

          // Final chunk
          if (currentChunkSize > 0) {
            try {
              const content = await zip.generateAsync({
                type: "blob",
                compression: "STORE",
                // compressionOptions: { level: 1 },
                streamFiles: true,
              });
              saveAs(content, `WorkOrder_${wo}_part${chunkIndex}.zip`);
            } catch (err) {
              console.error("Error finalizing last zip part:", err);
            }
          }
          setIsCreatingZip(false);
        }

        await largeDownloadsPromise;
      }
    } finally {
      setIsDownloading(false);
      setIsCreatingZip(false);
      setIsCheckAll(false);
      // setCurrentDownloadList([]);
      setArchivedImages([]);
      // setIsCheck([]);
      // setFilesDownloaded(0);
    }
  };

  const buttonText = (() => {
    const count = currentDownloadList.length;
    return count > 10 ? `Download Zip (${count})` : count > 0 ? `Download (${count})` : "Download";
  })();

  const listLength = currentDownloadList.length;
  const tippymsg = `Your download is underway. Attempting download: (${filesDownloaded}/${listLength}).`;

  const isBtnDisabled = isDownloading || !currentDownloadList.length || isCheck.length === 0;

  return (
    <>
      <Tippy
        content={
          <>
            <strong>Note:</strong> The download time may vary based on your internet connection
            speed, especially for larger files. Please avoid refreshing the page while waiting.
          </>
        }
        placement="bottom"
        theme="custom"
      >
        <button
          className={`btn-download ${isDownloading ? "is-downloading" : ""}`}
          onClick={handleDownload}
          disabled={isBtnDisabled}
        >
          {isCreatingZip ? (
            <Tippy content={tippymsg} placement="bottom" theme="custom">
            <span>Creating Zip...</span>
            </Tippy>
          ) : isDownloading ? (
            <Tippy content={tippymsg} placement="bottom" theme="custom">
              <span>Downloading...</span>
            </Tippy>
          ) : isBtnDisabled ? (
            "Download"
          ) : (
            buttonText
          )}
        </button>
      </Tippy>

      {failedFiles.length > 0 && !isDownloading && (
        <button
          className="btn-retry-failed"
          onClick={handleRetryFailed}
          title={`Retry ${failedFiles.length} failed file(s)`}
        >
          Retry Failed ({failedFiles.length})
        </button>
      )}
    </>
  );
}

export default DownloadButton;